/**
 * Stage atmosphere — per-stage environmental particle FX (rain, fog, embers,
 * neon dust, sea spray) that give the static parallax backdrops "life" without
 * new art. Pure config + a pure Phaser-emitter-config builder (no Phaser import),
 * so it is unit-testable in Node. StageScene owns the actual emitter creation.
 */

export type AtmosphereKind =
  | 'none'
  | 'rain'
  | 'drizzle'
  | 'fog'
  | 'embers'
  | 'neonDust'
  | 'seaSpray';

export interface AtmospherePreset {
  readonly kind: AtmosphereKind;
  /** 0..1 — scales emission rate (and, for rain, fall speed). */
  readonly intensity: number;
}

/** Per-stage atmosphere, keyed by stage number (1–9). Default = none. */
export const STAGE_ATMOSPHERE: Readonly<Record<number, AtmospherePreset>> = {
  1: { kind: 'neonDust', intensity: 0.4 }, // Camden Town — dusk market motes
  2: { kind: 'drizzle', intensity: 0.55 }, // Soho — wet neon night
  3: { kind: 'fog', intensity: 0.6 }, // Bermondsey Docks — river fog
  4: { kind: 'none', intensity: 0 }, // Northern Line — interior
  5: { kind: 'embers', intensity: 0.5 }, // Croydon Spice Lab — chemical haze/embers
  6: { kind: 'drizzle', intensity: 0.4 }, // Bethnal Green — overcast
  7: { kind: 'rain', intensity: 0.9 }, // Dover Coast — storm
  8: { kind: 'fog', intensity: 0.3 }, // Canary Wharf — cold mist
  9: { kind: 'rain', intensity: 0.6 }, // Westminster — night rain
};

export function atmosphereFor(stage: number): AtmospherePreset {
  return STAGE_ATMOSPHERE[stage] ?? { kind: 'none', intensity: 0 };
}

/**
 * Plain object matching the subset of Phaser's particle emitter config we use.
 * Kept structural (not importing Phaser.Types) so this module stays Phaser-free.
 */
export interface AtmosphereEmitterConfig {
  x: { min: number; max: number };
  y: number | { min: number; max: number };
  lifespan: number;
  speedX: { min: number; max: number };
  speedY: { min: number; max: number };
  scaleX: number;
  scaleY: number;
  alpha: { start: number; end: number };
  frequency: number;
  quantity: number;
  tint: number;
  blendMode: 'NORMAL' | 'ADD';
  gravityY: number;
}

export interface AtmosphereSpec {
  emitter: AtmosphereEmitterConfig;
  /** Render depth — overlay weather sits in front of gameplay but below HUD. */
  depth: number;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Build a Phaser emitter config for a preset across a `width`×`height` screen.
 * Returns null for `none` (or non-positive intensity) so the caller skips setup.
 * Pure — no Phaser, no randomness; deterministic for the given inputs.
 */
export function buildAtmosphereConfig(
  preset: AtmospherePreset,
  width: number,
  height: number,
): AtmosphereSpec | null {
  const t = Math.max(0, Math.min(1, preset.intensity));
  if (preset.kind === 'none' || t <= 0) return null;

  const fullW = { min: 0, max: width };

  switch (preset.kind) {
    case 'rain':
    case 'drizzle':
    case 'seaSpray': {
      const heavy = preset.kind === 'rain';
      const spray = preset.kind === 'seaSpray';
      return {
        depth: 7,
        emitter: {
          x: fullW,
          y: -10,
          lifespan: heavy ? 900 : 1300,
          speedX: { min: -40, max: -10 },
          speedY: { min: lerp(220, 520, t), max: lerp(320, 720, t) },
          scaleX: spray ? 0.6 : 0.4,
          scaleY: heavy ? 4 : spray ? 1 : 2.5,
          alpha: { start: heavy ? 0.55 : 0.4, end: 0.15 },
          // Lower frequency = more particles; scale inversely with intensity.
          frequency: Math.round(lerp(60, 14, t)),
          quantity: heavy ? 2 : 1,
          tint: spray ? 0xcfe0ec : 0x9fb4cc,
          blendMode: 'NORMAL',
          gravityY: 0,
        },
      };
    }
    case 'fog': {
      return {
        depth: 4,
        emitter: {
          x: fullW,
          y: { min: 0, max: height },
          lifespan: 6000,
          speedX: { min: 6, max: 22 },
          speedY: { min: -4, max: 4 },
          scaleX: 14,
          scaleY: 10,
          alpha: { start: lerp(0.04, 0.12, t), end: 0 },
          frequency: Math.round(lerp(500, 180, t)),
          quantity: 1,
          tint: 0xb8c2cc,
          blendMode: 'NORMAL',
          gravityY: 0,
        },
      };
    }
    case 'embers': {
      return {
        depth: 7,
        emitter: {
          x: fullW,
          y: { min: height * 0.4, max: height },
          lifespan: 2600,
          speedX: { min: -18, max: 18 },
          speedY: { min: -90, max: -40 },
          scaleX: 1.2,
          scaleY: 1.2,
          alpha: { start: lerp(0.3, 0.7, t), end: 0 },
          frequency: Math.round(lerp(220, 70, t)),
          quantity: 1,
          tint: 0x8cff6a, // sickly chemical green (Spice Lab)
          blendMode: 'ADD',
          gravityY: -10,
        },
      };
    }
    case 'neonDust': {
      return {
        depth: 6,
        emitter: {
          x: fullW,
          y: { min: 0, max: height },
          lifespan: 4200,
          speedX: { min: -14, max: 14 },
          speedY: { min: -16, max: 6 },
          scaleX: 0.9,
          scaleY: 0.9,
          alpha: { start: lerp(0.15, 0.4, t), end: 0 },
          frequency: Math.round(lerp(300, 120, t)),
          quantity: 1,
          tint: 0xff7ad0, // warm dusk neon
          blendMode: 'ADD',
          gravityY: 0,
        },
      };
    }
    default:
      return null;
  }
}
